import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit3, MoreHorizontal, Plus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getTreatmentCatalogItem,
  updateTreatmentCatalogItem,
  type TreatmentCatalogItem,
  type TreatmentCatalogUpdate,
} from '../../utils/treatmentCatalogApi';
import { addNewPrice, createCatalogItem, getCatalogItems, getPriceHistory } from '../../utils/billingApi';
import type { CatalogItem, PriceHistoryResponse } from '../../types/billing';
import { buildDisplayPrices, selectBestBillingItem } from '../../utils/billingCatalogHelpers';
import { TreatmentCatalogItemDrawer } from './components/TreatmentCatalogItemDrawer';

export const TreatmentCatalogShow: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;
  const { treatmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState<TreatmentCatalogItem | null>(null);
  const [billingItem, setBillingItem] = useState<CatalogItem | null>(null);
  const [standardBillingItem, setStandardBillingItem] = useState<CatalogItem | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showAddPrice, setShowAddPrice] = useState(false);
  const [newPriceValue, setNewPriceValue] = useState<string>('');
  const [addingPrice, setAddingPrice] = useState(false);

  const isEditOpen = searchParams.get('edit') === '1';

  const openEdit = () => {
    const next = new URLSearchParams(searchParams);
    next.set('edit', '1');
    setSearchParams(next);
  };

  const closeEdit = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const pid = practiceId;
    const id = treatmentId;
    if (!pid || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const t = await getTreatmentCatalogItem(pid, id);
        if (cancelled) return;
        setItem(t);

        const billing = await getCatalogItems(pid, 'Treatment', undefined, true, true);
        if (cancelled) return;
        const candidates = billing.filter(
          (b: any) =>
            (b as any).linked_treatment_id === id ||
            (b.category === 'Treatment' && b.name === t.name)
        );
        const linked = selectBestBillingItem(candidates, pid);
        setBillingItem(linked);

        if (linked?.id) {
          const standard =
            linked.based_on_standard_id
              ? billing.find((x) => x.id === linked.based_on_standard_id) || null
              : !linked.is_standard
                ? billing.find((x) => x.is_standard && x.category === linked.category && x.name === linked.name) || null
                : null;
          setStandardBillingItem(standard);

          const ph = await getPriceHistory(pid, linked.id);
          if (cancelled) return;
          setPriceHistory(ph);
        } else {
          setStandardBillingItem(null);
          setPriceHistory(null);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load treatment');
          setItem(null);
          setBillingItem(null);
          setStandardBillingItem(null);
          setPriceHistory(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, treatmentId]);

  const currentPrice = useMemo(() => {
    if (priceHistory?.current_price != null) return priceHistory.current_price;
    if (billingItem?.unit_price != null) return billingItem.unit_price;
    return null;
  }, [priceHistory, billingItem]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);

  const handleSave = async (data: TreatmentCatalogUpdate) => {
    if (!practiceId || !treatmentId) return;
    await updateTreatmentCatalogItem(practiceId, treatmentId, data);
    const refreshed = await getTreatmentCatalogItem(practiceId, treatmentId);
    setItem(refreshed);
  };

  const handleAddNewPrice = async () => {
    if (!practiceId || !billingItem?.id || !newPriceValue || !item) return;
    const priceNum = parseFloat(newPriceValue);
    if (Number.isNaN(priceNum) || priceNum <= 0) return;

    setAddingPrice(true);
    try {
      if (billingItem.is_standard) {
        const created = await createCatalogItem(practiceId, {
          code: billingItem.code || undefined,
          name: billingItem.name,
          category: billingItem.category,
          description: billingItem.description || undefined,
          unit_price: priceNum,
          unit_cost: billingItem.unit_cost,
          measure_unit: billingItem.measure_unit || undefined,
          taxable: billingItem.taxable,
          allow_price_override: billingItem.allow_price_override,
          display_order: billingItem.display_order,
          linked_treatment_id: item.id,
          based_on_standard_id: billingItem.id,
        });
        setBillingItem(created);
        setStandardBillingItem(billingItem);
        const ph = await getPriceHistory(practiceId, created.id);
        setPriceHistory(ph);
      } else {
        await addNewPrice(practiceId, billingItem.id, priceNum);
        const ph = await getPriceHistory(practiceId, billingItem.id);
        setPriceHistory(ph);
      }
      setShowAddPrice(false);
      setNewPriceValue('');
    } finally {
      setAddingPrice(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm text-gray-600 dark:text-gray-300">Loading…</div>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/settings/treatments-catalog')}
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Treatments
          </button>
          <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="text-sm text-red-600 dark:text-red-300">{error || 'Treatment not found'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/settings/treatments-catalog')}
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Treatments
        </button>

        <div className="mt-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{item.name}</h1>
              {item.is_active ? (
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                  Active
                </span>
              ) : (
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                  Inactive
                </span>
              )}
            </div>
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Inventory: {item.is_stock_tracked ? 'Stock-tracked' : 'Not tracked'}
            </div>
          </div>

          <button
            onClick={openEdit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            <Edit3 className="w-4 h-4" />
            Edit
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">Details</div>
              </div>
              <div className="px-6 py-4 space-y-4 text-sm text-gray-700 dark:text-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Type</div>
                    <div className="mt-1">{item.treatment_type || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Duration</div>
                    <div className="mt-1">{item.default_duration_minutes != null ? `${item.default_duration_minutes} min` : '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Sedation</div>
                    <div className="mt-1">{item.requires_sedation ? 'Required' : '—'}</div>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Description</div>
                  <div className="mt-1 whitespace-pre-wrap">{item.description || '—'}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Sedation notes</div>
                    <div className="mt-1 whitespace-pre-wrap">{item.sedation_notes || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Internal notes</div>
                    <div className="mt-1 whitespace-pre-wrap">{item.notes || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-visible">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Pricing</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Selling price is sourced from Billing Catalog.</div>
                </div>
                <button
                  onClick={() => setShowAddPrice(true)}
                  disabled={!billingItem?.id}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  title={billingItem?.is_standard ? 'Create practice override with custom price' : 'Add new price'}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {billingItem ? (
                    buildDisplayPrices({ billingItem, priceHistory, standardItem: standardBillingItem })
                      .sort((a, b) =>
                        a.is_current === b.is_current ? (a.created_at < b.created_at ? 1 : -1) : a.is_current ? -1 : 1
                      )
                      .map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 relative">
                          <td className="px-6 py-4">
                            <div className="text-blue-600 dark:text-blue-400 font-semibold">{formatPrice(p.unit_price)} USD</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">Per {billingItem.measure_unit || 'unit'}</div>
                          </td>
                          <td className="px-6 py-4 text-gray-500 dark:text-gray-400">—</td>
                          <td className="px-6 py-4">
                            {p.is_current ? (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md border border-green-200 dark:border-green-700 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                                Current
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400">
                                {(p as any).is_standard_reference ? 'Standard' : 'Archived'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(p.created_at)}</td>
                          <td className="px-6 py-4 text-right">
                            <button disabled className="p-1.5 text-gray-300 dark:text-gray-600 rounded-lg" title="Pricing actions are managed in Billing Catalog">
                              <MoreHorizontal className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        <p className="font-medium">No prices configured</p>
                        <p className="text-sm mt-1">Click + to add a price</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">At a glance</div>
              </div>
              <div className="px-6 py-4 space-y-2 text-sm text-gray-700 dark:text-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Selling price</span>
                  <span className="font-medium">{currentPrice == null ? '—' : formatPrice(Number(currentPrice))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Inventory</span>
                  <span className="font-medium">{item.is_stock_tracked ? 'Tracked' : 'Not tracked'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Type</span>
                  <span className="font-medium">{item.treatment_type || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Status</span>
                  <span className="font-medium">{item.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Price Modal */}
        {showAddPrice && billingItem?.id ? (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" onClick={() => setShowAddPrice(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {billingItem.is_standard ? 'Create Practice Override' : 'Add a price'}
                </h3>
                <button onClick={() => setShowAddPrice(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {billingItem.is_standard ? 'Your Practice Price' : 'Price'}
                </label>
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={newPriceValue}
                    onChange={(e) => setNewPriceValue(e.target.value)}
                    className="flex-1 px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    autoFocus
                  />
                  <span className="text-gray-500 dark:text-gray-400 font-medium">USD</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Per {billingItem.measure_unit || 'unit'}</p>
                {billingItem.is_standard ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Standard price: {formatPrice(billingItem.unit_price)}</p>
                ) : (
                  <div className="mb-4" />
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddPrice(false);
                      setNewPriceValue('');
                    }}
                    className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddNewPrice}
                    disabled={addingPrice || !newPriceValue}
                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {addingPrice ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : billingItem.is_standard ? (
                      'Create Override'
                    ) : (
                      'Add price'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <TreatmentCatalogItemDrawer isOpen={isEditOpen} item={item} onClose={closeEdit} onSave={handleSave} />
      </div>
    </div>
  );
};


